import { useEffect, useMemo, useState } from 'react';
import './LandingPage.css';
import { getHierarchyAnalytics } from '../api/client';
import MembersTable from './MembersTable';

const ROLE_LABELS = {
  1: 'State',
  2: 'District',
  3: 'Division',
  4: 'Sector',
};

export default function HierarchyDashboard({ session, onLogout }) {
  const { user, token } = session;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [sortBy, setSortBy] = useState('members');
  const [sortDir, setSortDir] = useState('desc');
  const [activeTab, setActiveTab] = useState('dashboard');

  const loadData = async (entityId) => {
    setLoading(true);
    try {
      const response = await getHierarchyAnalytics(token, entityId);
      setData(response);
      setError('');
    } catch (err) {
      setError(err.message || 'Unable to load hierarchy data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const totals = useMemo(() => ({
    units: data?.totals?.units ?? 0,
    members: data?.totals?.members ?? 0,
    over18: data?.totals?.over_18 ?? 0,
    participants: data?.totals?.participants ?? 0,
    collected: Number(data?.totals?.collected ?? 0),
    participatingUnits: data?.totals?.participating_units ?? 0,
  }), [data]);

  const sortedChildren = useMemo(() => {
    if (!data?.children || !Array.isArray(data.children)) {
      return [];
    }

    const extractValue = (child, key) => {
      const totals = child?.totals || {};
      switch (key) {
        case 'name':
          return child?.name || '';
        case 'members':
          return Number(totals.members ?? 0);
        case 'over18':
          return Number(totals.over_18 ?? 0);
        case 'participants':
          return Number(totals.participants ?? 0);
        case 'collected':
          return Number(totals.collected ?? 0);
        case 'participatingUnits':
          return Number(totals.participating_units ?? 0);
        default:
          return 0;
      }
    };

    const list = [...data.children];
    list.sort((a, b) => {
      if (sortBy === 'name') {
        const nameA = extractValue(a, 'name').toLowerCase();
        const nameB = extractValue(b, 'name').toLowerCase();
        if (nameA < nameB) return sortDir === 'asc' ? -1 : 1;
        if (nameA > nameB) return sortDir === 'asc' ? 1 : -1;
        return 0;
      }

      const valueA = extractValue(a, sortBy);
      const valueB = extractValue(b, sortBy);
      if (valueA === valueB) return 0;
      return sortDir === 'asc' ? valueA - valueB : valueB - valueA;
    });

    return list;
  }, [data, sortBy, sortDir]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const handleShareWhatsApp = () => {
    if (!data) {
      return;
    }

    const headingLabel = ROLE_LABELS[data.entity?.role_id] || 'Entity';
    const headingName = data.entity?.name ? ` - ${data.entity.name}` : '';
    const pathLine = data.path?.length ? data.path.map((crumb) => crumb.name).join(' › ') : '';
    const generatedAt = new Date().toLocaleString('en-IN');

    const formatNumber = (value) => Number(value ?? 0).toLocaleString('en-IN');

    const childLines = (sortedChildren.length ? sortedChildren : [{ name: 'No child entities available', totals: {} }])
      .slice(0, 200)
      .map((child, index) => {
        if (!child.id && child.name === 'No child entities available') {
          return child.name;
        }
        const totalsForChild = child.totals || {};
        return [
          `${index + 1}. ${child.name ?? '—'}`,
          `Members: ${formatNumber(totalsForChild.members)}`,
          `18+: ${formatNumber(totalsForChild.over_18)}`,
          `Participants: ${formatNumber(totalsForChild.participants)}`,
          `Collected: ₹${formatNumber(totalsForChild.collected)}`,
          `Participating units: ${formatNumber(totalsForChild.participating_units)}`,
        ].join('\n');
      })
      .join('\n\n');

    const totalsBlock = [
      'Totals',
      `Members: ${formatNumber(totals.members)}`,
      `18+: ${formatNumber(totals.over18)}`,
      `Participants: ${formatNumber(totals.participants)}`,
      `Collected: ₹${formatNumber(totals.collected)}`,
      `Participating units: ${formatNumber(totals.participatingUnits)}`,
    ].join('\n');

    const sections = [
      `${headingLabel} Report${headingName}`,
      pathLine ? `Path: ${pathLine}` : null,
      childLines,
      totalsBlock,
      `Generated: ${generatedAt}`,
    ].filter(Boolean);

    const message = sections.join('\n\n');
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleExportCsv = () => {
    if (!data) {
      return;
    }

    const headers = ['Name', 'Members', 'Members 18+', 'Participants', 'Total collected', 'Participating units'];
    const rows = sortedChildren.map((child) => {
      const totals = child.totals || {};
      return [
        child.name || '',
        totals.members ?? 0,
        totals.over_18 ?? 0,
        totals.participants ?? 0,
        Number(totals.collected ?? 0),
        totals.participating_units ?? 0,
      ];
    });

    const csv = [headers, ...rows]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filenameBase = data.entity?.name ? data.entity.name.replace(/\s+/g, '_').toLowerCase() : 'hierarchy_report';
    link.download = `${filenameBase}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const renderSortIndicator = (key) => {
    if (sortBy !== key) {
      return null;
    }
    return <span className="sort-indicator">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const handleBreadcrumbClick = (crumb, index) => {
    if (!data || index === data.path.length - 1) {
      return;
    }
    loadData(crumb.id);
  };

  const handleBack = () => {
    if (!data || !data.path || data.path.length < 2) {
      return;
    }
    const parent = data.path[data.path.length - 2];
    loadData(parent.id);
  };

  const handleChildClick = (childId) => {
    loadData(childId);
  };

  const currentLabel = ROLE_LABELS[data?.entity?.role_id] || 'Entity';
  const currentEntityId = data?.entity?.id ?? null;

  return (
    <div className="landing-container">
      <header className="landing-header">
        <div>
          <h1 className="landing-title">{currentLabel} dashboard</h1>
          <p className="landing-subtitle">
            Analyse participation and collections across the organisational hierarchy. Click rows to drill down.
          </p>
        </div>
        <button type="button" className="landing-logout" onClick={onLogout}>
          Log out
        </button>
      </header>

      <section className="landing-content">
        {error ? (
          <div className="inline-message error">
            <span>{error}</span>
            <button
              type="button"
              className="btn outline"
              onClick={() => loadData(data?.entity?.id)}
              disabled={loading}
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <div className="hierarchy-tablist" role="tablist">
              <button
                type="button"
                className={activeTab === 'dashboard' ? 'hierarchy-tab active' : 'hierarchy-tab'}
                role="tab"
                onClick={() => setActiveTab('dashboard')}
              >
                Dashboard
              </button>
              <button
                type="button"
                className={activeTab === 'hierarchy' ? 'hierarchy-tab active' : 'hierarchy-tab'}
                role="tab"
                onClick={() => setActiveTab('hierarchy')}
              >
                Drilldown table
              </button>
              <button
                type="button"
                className={activeTab === 'members' ? 'hierarchy-tab active' : 'hierarchy-tab'}
                role="tab"
                onClick={() => setActiveTab('members')}
              >
                Members table
              </button>
            </div>

            {activeTab === 'dashboard' ? (
              <div className="hierarchy-dashboard-tab">
                <div className="summary-grid">
                  <div className="summary-card">
                    <h3>Total units</h3>
                    <div className="value">{totals.units}</div>
                    <div className="meta">Units within this scope</div>
                  </div>
                  <div className="summary-card">
                    <h3>Total members</h3>
                    <div className="value">{totals.members}</div>
                    <div className="meta">All members registered</div>
                  </div>
                  <div className="summary-card">
                    <h3>Members aged 18+</h3>
                    <div className="value">{totals.over18}</div>
                    <div className="meta">Adults within the scope</div>
                  </div>
                  <div className="summary-card">
                    <h3>Participants</h3>
                    <div className="value">{totals.participants}</div>
                    <div className="meta">Unique members who contributed</div>
                  </div>
                  <div className="summary-card">
                    <h3>Total collected</h3>
                    <div className="value">₹{totals.collected.toLocaleString()}</div>
                    <div className="meta">Sadaqa collected in this scope</div>
                  </div>
                  <div className="summary-card">
                    <h3>Participating units</h3>
                    <div className="value">{totals.participatingUnits}</div>
                    <div className="meta">Units with at least one contribution</div>
                  </div>
                </div>

                <div className="hierarchy-dashboard-actions">
                  <button type="button" className="btn outline" onClick={handleShareWhatsApp}>
                    Share dashboard to WhatsApp
                  </button>
                </div>
              </div>
            ) : activeTab === 'hierarchy' ? (
              <div className="hierarchy-panel">
                {loading ? (
                  <div className="hierarchy-loading">Loading hierarchy…</div>
                ) : (
                  <>
                    {data?.path?.length ? (
                      <div className="hierarchy-breadcrumbs">
                        {data.path.map((crumb, index) => (
                          <button
                            key={crumb.id}
                            type="button"
                            className={index === data.path.length - 1 ? 'crumb current' : 'crumb'}
                            onClick={() => handleBreadcrumbClick(crumb, index)}
                          >
                            {crumb.name}
                          </button>
                        ))}
                      </div>
                    ) : null}

                    {data?.path?.length > 1 ? (
                      <button type="button" className="btn outline back-btn" onClick={handleBack}>
                        ← Back to {data.path[data.path.length - 2].name}
                      </button>
                    ) : null}

                    <div className="hierarchy-tools">
                      <div className="hierarchy-tools-actions">
                        <button type="button" className="btn outline" onClick={handleExportCsv}>
                          Export CSV
                        </button>
                        <button type="button" className="btn primary" onClick={handleShareWhatsApp}>
                          Share to WhatsApp
                        </button>
                      </div>
                    </div>

                    <div className="hierarchy-table-wrapper">
                      <table className="hierarchy-table">
                        <thead>
                          <tr>
                            <th className="sortable" onClick={() => handleSort('name')}>
                              Name
                              {renderSortIndicator('name')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('members')}>
                              Total members
                              {renderSortIndicator('members')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('over18')}>
                              Members 18+
                              {renderSortIndicator('over18')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('participants')}>
                              Participants
                              {renderSortIndicator('participants')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('collected')}>
                              Total collected
                              {renderSortIndicator('collected')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('participatingUnits')}>
                              Participating units
                              {renderSortIndicator('participatingUnits')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedChildren.length ? (
                            sortedChildren.map((child) => (
                              <tr key={child.id} onClick={() => handleChildClick(child.id)}>
                                <td>
                                  <div className="entity-name">
                                    <span className="entity-label">{ROLE_LABELS[child.role_id] || 'Entity'}</span>
                                    <span>{child.name}</span>
                                  </div>
                                </td>
                                <td>{child.totals?.members ?? 0}</td>
                                <td>{child.totals?.over_18 ?? 0}</td>
                                <td>{child.totals?.participants ?? 0}</td>
                                <td>₹{Number(child.totals?.collected ?? 0).toLocaleString()}</td>
                                <td>{child.totals?.participating_units ?? 0}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6} className="empty-state">
                                No sub-entities found under this level.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <MembersTable
                key={`members-${data?.entity?.id ?? 'root'}`}
                session={session}
                entityId={data?.entity?.id ?? null}
              />
            )}
          </>
        )}
      </section>
    </div>
  );
}
